import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  useFailedSubmission,
  useIgnoreFailedSubmission,
  useReprocessFailedSubmission,
  useUpdateFailedSubmissionNotes,
} from '@/hooks/use-failed-submissions';
import { AlertCircle, ArrowLeft, CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

const errorTypeLabels: Record<string, string> = {
  missing_group_id: 'Groep ID ontbreekt',
  group_not_found: 'Groep niet gevonden',
  missing_email: 'Email ontbreekt',
  processing_error: 'Verwerkingsfout',
  validation_error: 'Validatiefout',
};

const statusLabels: Record<string, string> = {
  pending: 'In behandeling',
  resolved: 'Opgelost',
  ignored: 'Genegeerd',
};

export function FailedSubmissionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: failed, isLoading } = useFailedSubmission(id!);
  const reprocess = useReprocessFailedSubmission();
  const ignore = useIgnoreFailedSubmission();
  const updateNotes = useUpdateFailedSubmissionNotes();

  const [notes, setNotes] = useState('');
  const [isEditingPayload, setIsEditingPayload] = useState(false);
  const [editedPayload, setEditedPayload] = useState('');

  useEffect(() => {
    if (failed) {
      // eslint-disable-next-line react/set-state-in-effect -- Hydrates the editable note from the asynchronously loaded submission.
      setNotes(failed.notes ?? '');
    }
  }, [failed]);

  const handleReprocess = async () => {
    if (!id) return;

    let overridePayload: Record<string, unknown> | undefined;

    if (isEditingPayload && editedPayload) {
      try {
        overridePayload = JSON.parse(editedPayload);
      } catch (_e) {
        toast.error('Ongeldige JSON in payload');
        return;
      }
    }

    await reprocess.mutateAsync({
      id,
      overridePayload,
      resolvedBy: 'user',
    });

    navigate('/mislukte-inzendingen');
  };

  const handleIgnore = async () => {
    if (!id) return;

    await ignore.mutateAsync({
      id,
      notes: notes || undefined,
    });

    navigate('/mislukte-inzendingen');
  };

  const handleSaveNotes = async () => {
    if (!id) return;
    await updateNotes.mutateAsync({ id, notes });
    // Intentionally do NOT reset notes here — clearing the textarea after save
    // is surprising and forces the user to retype the note if they then click
    // "ignore" (which also uses the same notes state).
  };

  const handleEditPayload = () => {
    if (failed) {
      setEditedPayload(JSON.stringify(failed.rawPayload, null, 2));
      setIsEditingPayload(true);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!failed) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Link to="/mislukte-inzendingen">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-3xl font-bold">Inzending niet gevonden</h1>
        </div>
      </div>
    );
  }

  const statusIcon =
    failed.status === 'resolved' ? (
      <CheckCircle2 className="h-5 w-5 text-green-600" />
    ) : failed.status === 'ignored' ? (
      <XCircle className="h-5 w-5 text-gray-600" />
    ) : (
      <AlertCircle className="h-5 w-5 text-red-600" />
    );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Link to="/mislukte-inzendingen">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">Mislukte Inzending</h1>
          <p className="text-muted-foreground">
            {new Date(failed.createdAt).toLocaleDateString('nl-NL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {statusIcon}
          <span className="font-medium">{statusLabels[failed.status]}</span>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
            <CardDescription>Informatie over de mislukte inzending</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground">Type fout</Label>
              <div className="mt-1">
                <Badge variant="outline">{errorTypeLabels[failed.errorType] || failed.errorType}</Badge>
              </div>
            </div>

            <div>
              <Label className="text-muted-foreground">Foutmelding</Label>
              <div className="mt-1 rounded-md bg-muted p-3 text-sm">{failed.errorMessage}</div>
            </div>

            {failed.externalSubmissionId && (
              <div>
                <Label className="text-muted-foreground">External Submission ID</Label>
                <div className="mt-1">
                  <code className="text-sm">{failed.externalSubmissionId}</code>
                </div>
              </div>
            )}

            {failed.formId && (
              <div>
                <Label className="text-muted-foreground">Form ID</Label>
                <div className="mt-1">
                  <code className="text-sm">{failed.formId}</code>
                </div>
              </div>
            )}

            <div>
              <Label className="text-muted-foreground">Provider</Label>
              <div className="mt-1">{failed.provider}</div>
            </div>

            <div>
              <Label className="text-muted-foreground">Aantal pogingen</Label>
              <div className="mt-1">{failed.attemptCount}</div>
            </div>

            {failed.resolvedSubmission && (
              <div>
                <Label className="text-muted-foreground">Opgelost met</Label>
                <div className="mt-1">
                  <Link
                    to={`/groepen/${failed.resolvedSubmission.groupId}/registraties/${failed.resolvedSubmission.registrationId}`}
                    className="text-primary hover:underline"
                  >
                    Registratie {failed.resolvedSubmission.registration?.email}
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Acties</CardTitle>
            <CardDescription>Beheer deze mislukte inzending</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {failed.status === 'pending' && (
              <>
                <Button className="w-full" onClick={handleReprocess} disabled={reprocess.isPending}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {reprocess.isPending ? 'Aan het verwerken...' : 'Opnieuw verwerken'}
                </Button>

                <Button variant="outline" className="w-full" onClick={handleEditPayload}>
                  Payload bewerken & opnieuw verwerken
                </Button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="secondary" className="w-full">
                      <XCircle className="mr-2 h-4 w-4" />
                      Negeren
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Inzending negeren?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Deze inzending wordt gemarkeerd als genegeerd en zal niet meer worden verwerkt.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="my-4">
                      <Label>Notitie (optioneel)</Label>
                      <Textarea
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        placeholder="Waarom wordt deze inzending genegeerd?"
                        className="mt-2"
                      />
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuleren</AlertDialogCancel>
                      <AlertDialogAction onClick={handleIgnore} disabled={ignore.isPending}>
                        {ignore.isPending ? 'Negeren...' : 'Bevestigen'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}

            <div className="pt-4">
              <Label>Notities</Label>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Voeg notities toe..."
                className="mt-2"
                rows={4}
              />
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={handleSaveNotes}
                disabled={updateNotes.isPending || !notes}
              >
                Notities opslaan
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {failed.errorStack && (
        <Card>
          <CardHeader>
            <CardTitle>Stack Trace</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="overflow-x-auto max-w-full rounded-md bg-muted p-4 text-xs whitespace-pre-wrap break-all">
              <code>{failed.errorStack}</code>
            </pre>
          </CardContent>
        </Card>
      )}

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle>Raw Payload</CardTitle>
          <CardDescription>
            {isEditingPayload
              ? 'Bewerk de payload hieronder om fouten te corrigeren'
              : 'De originele data van de webhook'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isEditingPayload ? (
            <div className="space-y-4">
              <Textarea
                value={editedPayload}
                onChange={e => setEditedPayload(e.target.value)}
                className="font-mono text-xs"
                rows={20}
              />
              <div className="flex gap-2">
                <Button onClick={handleReprocess} disabled={reprocess.isPending}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {reprocess.isPending ? 'Aan het verwerken...' : 'Verwerken met aangepaste payload'}
                </Button>
                <Button variant="outline" onClick={() => setIsEditingPayload(false)}>
                  Annuleren
                </Button>
              </div>
            </div>
          ) : (
            <pre className="overflow-x-auto max-w-full rounded-md bg-muted p-4 text-xs whitespace-pre-wrap break-all">
              <code>{JSON.stringify(failed.rawPayload, null, 2)}</code>
            </pre>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
